import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import clsx from "clsx";

import {Checkbox, FormControlLabel, Tooltip} from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton';
import Chip from "@material-ui/core/Chip";

import BanksHint from '../../BanksHint';
import PrimaryButton from '../../../../../../../../../../components/Buttons/PrimaryButton';
import {BcaDeleteIcon} from "../../../../../../../../../../images/BcaIcon";
import {DepotDocumentIdentifier} from "../../utils";

import useStyles from './styles'

const getDocumentId = (isOnlineDocument, document) => isOnlineDocument ? document.external_id : document.id;

function DocumentsSectionV2(props) {

  const {
    objectType,
    bankId,
    bankName,
    documents,
    allOptional,
    optionalDocuments,
    downloadDocuments,
    eSignDocuments,
    onDistributionSettingsChange,
    onPreviewDocumentSelect,
    errors,
    isOnline,
    onlineDocuments,
    bankNameVisible,
    singleBankSelected,
    isGeneralBank,
    onDocumentFilterModalSwitch,
    onDeleteOptionalDocument,
    additionalBankText,
  } = props;

  const classes = useStyles();

  const handlePreviewDocumentSelect = (document) => (event) => {
    onPreviewDocumentSelect && onPreviewDocumentSelect(document, bankId, objectType)
  };

  const handleDistributionSettingsChange = (distributionType, documentId) => (event) => {
    event.stopPropagation();
    onDistributionSettingsChange && onDistributionSettingsChange(documentId, distributionType, event.target.checked, bankId, objectType)
  };

  const renderDocument = ({document, isOnlineDocument=false}) => {
    const docId = getDocumentId(isOnlineDocument, document);

    let SignCheckboxControl = isOnlineDocument ? (
        <Tooltip
          classes={{tooltip: classes.tooltip, popper: classes.tooltipPopper}}
          title={"Pflichtdokumente können nicht abgewält werden"}
          placement={"top"}
          arrow
        >
          <span>
            <Checkbox color="primary" checked={true} disabled={true} />
          </span>
        </Tooltip>
      ) : (
        <Checkbox color="primary"
          checked={eSignDocuments.includes(new DepotDocumentIdentifier(bankId, docId, objectType))}
          onChange={handleDistributionSettingsChange('eSign', docId)} />
      );

    const docErrors = _.get(errors, `${objectType}.${bankId}.${docId}`);

    return (
      <div className={classes.documentItem} key={docId}>
        <div>
          <span title={document.name}>{ document.name }</span>
          {!isOnlineDocument ? (
            <>
              {optionalDocuments.includes(document) && (
                <span style={{marginRight: 10}}>
                  <IconButton
                    style={{fontSize: 15}}
                    onClick={() => onDeleteOptionalDocument(bankId, docId, objectType)}
                    color={'secondary'}
                  >
                    <BcaDeleteIcon />
                  </IconButton>
                </span>
              )}
            </>
          ) : (<>
            {isOnlineDocument && (
              <span style={{marginRight: 16}}>
                <Chip label={'Online'} className={classes.onlineChip} />
              </span>
            )}
          </>)}
          <span className={classes.previewButton} onClick={handlePreviewDocumentSelect(document)}>
            <i className="far fa-eye fa-lg" style={{marginRight: 8}} />Vorschau
          </span>
        </div>
        <div className={classes.checkboxes}>
          <div>
            <FormControlLabel
              control={
                <Checkbox color="primary"
                  checked={downloadDocuments.includes(new DepotDocumentIdentifier(bankId, docId, objectType))}
                  onChange={handleDistributionSettingsChange('download', docId)}
                />
              }
              classes={{
                root: classes.formControlLabelRoot,
                disabled: classes.formControlLabelDisabled
              }}
            />
          </div>
          <div>
            {document.sign_possible && (
              <FormControlLabel
                control={SignCheckboxControl}
                classes={{
                  root: classes.formControlLabelRoot,
                  disabled: classes.formControlLabelDisabled
                }}
              />
            )}
          </div>
          {!!docErrors && (
            <div style={{flexBasis: '100%'}}>
              <span className={clsx(classes.errorMessage, 'document-distribution-item-error')}>
                { docErrors }
              </span>
            </div>
          )}
        </div>
      </div>
    )
  };

  const onlineDocs = (isOnline && onlineDocuments || []).map((doc) => ({...doc, isOnlineDocument: true}));
  const documentsForDownloadSelection = [...onlineDocs, ...optionalDocuments, ...documents];
  const documentsForEsignSelection = _.filter(documentsForDownloadSelection, (doc) => doc.isOnlineDocument || doc.sign_possible);

  const handleAllCheckboxChanged = (distributionType, selected, availableDocs) => (event) => {
    const checked = event.target.checked;

    availableDocs.forEach(doc => {
      // block deselect online from E-Sign
      if(!(doc.isOnlineDocument && distributionType === 'eSign')){
        const docId = getDocumentId(doc.isOnlineDocument, doc);
        const isDocSelected = selected.includes(new DepotDocumentIdentifier(bankId, docId, objectType));

        if(isDocSelected !== checked){
          handleDistributionSettingsChange(distributionType, docId)(event)
        }
      }
    });

  };

  return (
    <div className={classes.container} key={bankName} style={{paddingBottom: bankNameVisible ? 5 : 0}}>
      {
        bankName && bankNameVisible && <div className={classes.titleSection}><span className={classes.bankName}> { bankName }{!isGeneralBank && <>-Formulare</>} </span></div>
      }

      {
        ! isGeneralBank && bankName && bankNameVisible && !singleBankSelected &&
        <div className={classes.titleHintSection}>
          <BanksHint bankName={bankName} isBankOnline={isOnline} additionalBankText={additionalBankText} />
        </div>
      }

      <div className={classes.documentsSection}>
        <div className={clsx(classes.documentItem, 'headline')}>
          <div style={{border: 'none'}}>
            <span><b>Dokumente</b></span>
          </div>
          <div className={classes.checkboxes}>
            <div>
              <span>
                <Checkbox
                  color="primary"
                  checked={downloadDocuments.length === documentsForDownloadSelection.length}
                  onChange={handleAllCheckboxChanged('download', downloadDocuments, documentsForDownloadSelection)}
                />
                Download
              </span>
            </div>
            <div>
              <span>
                <Checkbox
                  color="primary"
                  checked={eSignDocuments.length === documentsForEsignSelection.length}
                  onChange={handleAllCheckboxChanged('eSign', eSignDocuments, documentsForEsignSelection)}
                />
                E-Sign
              </span>
            </div>
          </div>

        </div>
      </div>
      <div className={classes.documentsSection}>
        {onlineDocs.map(doc => renderDocument({document:doc, isOnlineDocument:true}))}
        {(documents || []).map(doc => renderDocument({document:doc}))}
        {!_.isEmpty(allOptional || []) && (
          <>
            <div className={classes.titleSection}>
              <span className={classes.bankName} style={{margin: '15px 0 13px'}}> Zusätzliche Dokumente </span>
            </div>
            {optionalDocuments.map(doc => renderDocument({document:doc}))}
          </>
        )}
      </div>
      {!isGeneralBank && bankId && (
        <div className={classes.documentsManagementButton}>
          <PrimaryButton
            text={'Dokument(e) hinzufügen'}
            icon={<i className={'fal fa-plus'} />}
            onButtonClick={() => onDocumentFilterModalSwitch(bankId, objectType)}
          />
        </div>
      )}
    </div>
  )
}

DocumentsSectionV2.propTypes = {
  /**Bank name, that will be displayed inside section title */
  bankName: PropTypes.string,

  /**List of available documents */
  documents: PropTypes.arrayOf(PropTypes.object),

  /**Selected documents change handler */
  onSelectedDocumentsChange: PropTypes.func,

  /**Preview document select handler */
  onPreviewDocumentSelect: PropTypes.func,

  /**Flag, that indicate, is bank in General constants  */
  isGeneralBank: PropTypes.bool,

  /**Optional documents modal open handler */
  onDocumentFilterModalSwitch: PropTypes.func
};

DocumentsSectionV2.defaultProps = {
  documents: [],
};

export default DocumentsSectionV2

